/**
 * External dependencies
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Internal dependencies
 */
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import ImageFilePicker from '@Components/ImageFilePicker';
import { type AssetTagValue } from '@Components/AssetTagInput';
import { AspectRatio } from '@Components/AspectRatio';
import { Skeleton } from '@Components/Skeleton';
import { CheckIcon } from 'lucide-react';
import { useAssets } from '../../hooks/useAssets';
import { useUploadImagesToOrgMutation } from '../../hooks/useCloudinaryUpload';
import { cn } from '../../utils/cn';

export interface FeaturedImageDialogProps {
  /** Display name shown in the dialog title (e.g. venue or space name) */
  name: string;
  resourceType: 'venue' | 'space' | 'event' | 'card';
  /** The current featured asset ID, if any */
  currentFeaturedAssetId: string | null | undefined;
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when the dialog should open or close */
  onOpenChange: (open: boolean) => void;
  /**
   * Called with the selected asset ID when the user confirms.
   * The caller is responsible for persisting this (e.g. updating venue or space).
   * Should return a promise that resolves when the save is complete.
   */
  onSave: (assetId: string) => Promise<void>;
}

const FeaturedImageFormSchema = z.object({
  featuredAssetId: z.string().optional().nullable(),
});

type FeaturedImageFormValues = z.infer<typeof FeaturedImageFormSchema>;

export default function FeaturedImageDialog({
  name,
  resourceType,
  currentFeaturedAssetId,
  isOpen,
  onOpenChange,
  onSave,
}: FeaturedImageDialogProps) {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingTags, setPendingTags] = useState<AssetTagValue[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: assets,
    status,
    refetch,
  } = useAssets({
    enabled: isOpen,
  });

  const featuredAssetForm = useForm<FeaturedImageFormValues>({
    resolver: zodResolver(FeaturedImageFormSchema),
    defaultValues: {
      featuredAssetId: currentFeaturedAssetId ?? null,
    },
  });

  const { mutateAsync: uploadImages, isPending: isUploading } =
    useUploadImagesToOrgMutation();

  const isBusy = isUploading || isSaving;

  // Fresh state on EVERY open, synced to the current target. The dialog is
  // mounted once and reused across targets (e.g. the content editor's
  // cards), and parents open it by flipping `isOpen` — a path Radix's
  // onOpenChange never fires for — so mount-time defaults and close-time
  // resets both go stale. Also re-syncs if the target changes while open.
  useEffect(() => {
    if (!isOpen) return;
    setPendingFile(null);
    setPendingTags([]);
    featuredAssetForm.reset({
      featuredAssetId: currentFeaturedAssetId ?? null,
    });
    featuredAssetForm.clearErrors('root');
  }, [isOpen, currentFeaturedAssetId, featuredAssetForm]);

  const handleFileChange = (file: File | null) => {
    setPendingFile(file);
    if (file) {
      featuredAssetForm.setValue('featuredAssetId', null, {
        shouldDirty: true,
      });
    }
  };

  const onSubmit = async (values: FeaturedImageFormValues) => {
    featuredAssetForm.clearErrors('root');

    let assetIdToUse = values.featuredAssetId || null;

    if (pendingFile) {
      try {
        const result = await uploadImages({
          orgId: '',
          files: [pendingFile],
          assetTags: pendingTags.length > 0 ? pendingTags : undefined,
        });

        const cloudinaryItems = Array.isArray(result) ? result : [result];
        const publicId = (cloudinaryItems[0] as any)?.public_id;

        if (!publicId) {
          featuredAssetForm.setError('root', {
            type: 'server',
            message:
              'Upload succeeded but could not identify the image. Please try again.',
          });
          return;
        }

        const { data: freshAssets } = await refetch();

        const matchedAsset = (freshAssets as any[])?.find(
          (a: any) => a.cloudinaryId === publicId,
        );

        if (!matchedAsset?.id) {
          featuredAssetForm.setError('root', {
            type: 'server',
            message:
              'Upload succeeded but the asset could not be found. Please try again.',
          });
          return;
        }

        assetIdToUse = matchedAsset.id;
      } catch (error: any) {
        console.error('Error uploading image:', error);
        featuredAssetForm.setError('root', {
          type: 'server',
          message:
            error?.message || 'Failed to upload image. Please try again.',
        });
        return;
      }
    }

    if (!assetIdToUse) return;

    try {
      setIsSaving(true);
      await onSave(assetIdToUse);
      setPendingFile(null);
      setPendingTags([]);
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving featured image:', error);
      featuredAssetForm.setError('root', {
        type: 'server',
        message:
          'We were unable to update the featured image. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const selectedAssetId = featuredAssetForm.watch('featuredAssetId');
  const hasSelection = !!pendingFile || !!selectedAssetId;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      {/* Pinned header/footer layout: the panel is a max-h-[95vh] column,
          only the body scrolls. overrideSrollBehavior drops the kit's own
          overflow-y-auto, which would scroll the whole panel instead. */}
      <DialogContent
        overrideSrollBehavior
        className="flex max-h-[95vh] flex-col overflow-hidden sm:max-w-3xl"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>Select featured image for {name}</DialogTitle>
          <p className="text-sm text-zinc-500">
            Choose an image to feature for this {resourceType}.
          </p>
        </DialogHeader>

        <Form {...featuredAssetForm}>
          <form
            onSubmit={featuredAssetForm.handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col space-y-6"
          >
            {/* p-1 keeps selection rings/focus outlines visible at the
                scroll container's clipped edges. */}
            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-1">
              {featuredAssetForm.formState.errors.root?.message && (
                <div className="mb-4">
                  <Alert variant="destructive">
                    <AlertTitle>Something went wrong</AlertTitle>
                    <AlertDescription>
                      {featuredAssetForm.formState.errors.root?.message}
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* Upload new image */}
              <div>
                <div className="mb-2 text-sm font-medium text-zinc-700">
                  Upload new image:
                </div>
                <ImageFilePicker
                  inputId="featured-image-upload-input"
                  file={pendingFile}
                  onFileChange={handleFileChange}
                  tags={pendingTags}
                  onTagsChange={setPendingTags}
                />
              </div>

              {/* OR divider */}
              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-zinc-200" />
                <span className="text-sm text-zinc-400">OR</span>
                <div className="h-px flex-1 bg-zinc-200" />
              </div>

              {/* Choose from gallery */}
              <div>
                <div className="mb-2 text-sm font-medium text-zinc-700">
                  Choose from your gallery:
                </div>
                <FormField
                  control={featuredAssetForm.control}
                  name="featuredAssetId"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          {/* No inner max-h/scroll: the dialog body is the
                              single scroll area. */}
                          <div className="grid grid-cols-3 gap-3 p-1">
                            {status === 'pending' ? (
                              <>
                                <Skeleton className="aspect-square w-full rounded-md" />
                                <Skeleton className="aspect-square w-full rounded-md" />
                                <Skeleton className="aspect-square w-full rounded-md" />
                              </>
                            ) : !assets || assets.length === 0 ? (
                              <div className="col-span-3 flex h-28 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-100 text-center text-sm text-zinc-500">
                                <div className="font-medium text-zinc-700">
                                  No uploaded images
                                </div>
                              </div>
                            ) : (
                              assets.map((asset) => {
                                const featured =
                                  !pendingFile && field.value === asset.id;
                                const tagNames =
                                  asset.tags?.map((t) => t.tag.name) ?? [];
                                return (
                                  <button
                                    key={asset.id}
                                    type="button"
                                    onClick={() => {
                                      setPendingFile(null);
                                      field.onChange(
                                        field.value === asset.id
                                          ? null
                                          : asset.id,
                                      );
                                    }}
                                    className={cn(
                                      'group relative flex w-full flex-col overflow-hidden rounded-md border bg-zinc-200 transition',
                                      featured
                                        ? 'border-primary ring-4 ring-[#CAAB6A]'
                                        : 'border-transparent hover:border-zinc-300',
                                    )}
                                  >
                                    <AspectRatio
                                      ratio={1}
                                      className="relative w-full"
                                    >
                                      <CloudinaryImage
                                        publicId={asset.cloudinaryId}
                                        mode="original"
                                        className="h-full w-full object-contain"
                                      />
                                      {featured && (
                                        <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#CAAB6A] bg-black">
                                          <CheckIcon className="h-4 w-4 text-[#CAAB6A]" />
                                        </div>
                                      )}
                                    </AspectRatio>
                                    {tagNames.length > 0 && (
                                      <div className="flex flex-wrap gap-1 bg-white px-1.5 py-1.5">
                                        {tagNames.map((name) => (
                                          <span
                                            key={name}
                                            className="bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-[10px] font-medium"
                                          >
                                            {name}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            </div>
            <DialogFooter className="shrink-0">
              <DialogClose asChild>
                <Button type="button" variant="link" disabled={isBusy}>
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type="submit"
                variant="primary"
                loading={isBusy}
                disabled={isBusy || !hasSelection}
              >
                {pendingFile ? 'Upload and use image' : 'Use image'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
